/**
 * Fleet registration and heartbeat: how a workbench announces itself to the
 * platform and how the platform describes one back to the console. Mirrors
 * `src/platform/fleet.ts` of the AI Workbench (branch
 * `feat/agent-node-supervisor`): the same fields, limits and defaults.
 *
 * Device kinds: `desktop` is the Electron app a person runs (`kind` absent
 * reads as this); `node` is the headless supervisor (`mojoup-node`), which
 * also sends `capabilities`, its manifest (./nodeManifest.ts), and carries
 * the manifest's revision and its load on every heartbeat.
 *
 * A board kept on the workbench (AIOE 0.32.0, C-26; optional, so no version bump): a workspace
 * that names a project may carry `localBoard`, "this repository's board lives here, N agents are
 * busy on it, and its own tracker mirror writes GitHub owner/repo". The platform keeps it per
 * device and repository, fresh per report, and counts it only while the device is online. A
 * platform tracker for a target a live mirror already writes is refused (409 `mirror_conflict`,
 * docs/workbench-integration.md section 25). A heartbeat may carry `localBoards` to refresh the
 * agent counts and the mirror between registrations.
 *
 * A merge, learnt at the next beat (AIOE 0.36.0, optional, no version bump): the heartbeat answer carries
 * `merged: [{ projectId, mergedInto, mergedIntoName }]`, one entry for each workspace of the device whose
 * `projectId` is a merged-away project (chains followed to the survivor), so an idle desktop need not wait
 * for its next `POST /projects/v1`. Absent when none is. Nothing else on the answer changes.
 *
 * Node management (AIOE 0.37.0, D257; optional, so no version bump; ./deviceOrders.ts, docs/workbench-integration.md section 34):
 * the heartbeat answer carries `orders: DeviceOrder[]` (at most 50 pending orders for the device, oldest
 * first, repeated until the machine answers each or the order expires). The heartbeat body carries
 * `orders: DeviceOrderAnswer[]`, one answer for each order the machine has finished and the platform still repeats
 * (`done`, `failed` or `refused`, with a sentence). A registration and a heartbeat may carry `facts`: the machine's
 * models, runtimes and DLP state; the platform keeps the latest on the device (`FleetDevice.facts`).
 */
import { deviceClearSchema } from "./deviceClear.js";
import { z } from "zod";
import { fleetAgentCommandSchema, fleetAgentsReportSchema, MAX_AGENT_COMMANDS } from "./agentRegistry.js";
import { deviceFactsSchema, deviceOrderAnswerSchema, deviceOrderSchema, MAX_DEVICE_ORDERS } from "./deviceOrders.js";
import { updateOrderSchema } from "./releases.js";
import { nodeManifestSchema } from "./nodeManifest.js";
import { fleetManifestRefSchema, fleetPolicyReportSchema, policyManifestOrderSchema } from "./policyManifest.js";
import { remoteSessionsReportSchema } from "./remoteControl.js";
import { deviceSealingKeySchema } from "./secretSeal.js";
import { trackerProviderSchema } from "./trackers.js";
export const FLEET_SCHEMA_VERSION = 1;
export const workbenchPlatformSchema = z.enum(["windows", "linux", "darwin"]);
/** Where a root lives: this machine, a WSL distro, a dev container, a headless node, or a machine over SSH. */
export const workspaceTargetSchema = z.enum(["local", "wsl", "devcontainer", "node", "ssh"]);
/**
 * Whether the host holding a root is running: a WSL distro or dev container that is stopped or
 * still starting, or one that failed. Absent means running (a workbench before the field, or a
 * local root).
 */
export const workspaceHostStateSchema = z.enum(["ready", "starting", "provisioning", "stopped", "error", "exited"]);
/** Whether the target (an SSH host, a node) answers: absent reads as available. */
export const workspaceTargetStateSchema = z.enum(["available", "stopped", "unavailable"]);
/** What kind of workbench registers: the desktop app, or a headless node. */
export const deviceKindSchema = z.enum(["desktop", "node"]);
/**
 * Device posture (mesh round 3), optional on registration, re-sent when it changes: what the
 * machine can say about itself without a management agent. The mesh's grants may require it.
 */
export const devicePostureSchema = z.object({
    os: z.enum(["windows", "darwin", "linux", "ios", "android"]).optional(),
    osVersion: z.string().max(40).optional(),
    /** When the OS last installed updates, as the machine reports it. */
    patchedAt: z.string().optional(),
    diskEncrypted: z.boolean().optional(),
    firewall: z.boolean().optional(),
    screenLock: z.boolean().optional(),
    secureBoot: z.boolean().optional(),
    /** When the machine measured these. */
    measuredAt: z.string().optional(),
});
/**
 * What a workspace has as customisations (D219), optional on the registration (the heartbeat carries
 * no workspaces; register again when they change): plugins and hooks by
 * identity only (a hook's command may carry a secret, so its sha256 travels as `commandHash`),
 * and counts of the rest. The console shows what each machine has and where a plugin is installed.
 */
export const workspaceCustomisationsSchema = z.object({
    plugins: z.array(z.object({ id: z.string().max(400), enabled: z.boolean(), scope: z.enum(["just-you", "repository"]), runtimes: z.array(z.string().max(40)).max(8) })).max(200).default([]),
    hooks: z.array(z.object({ event: z.string().max(40), matcher: z.string().max(200).optional(), runtimes: z.array(z.string().max(40)).max(8), scope: z.enum(["just-you", "repository"]), fromOrganisation: z.boolean(), commandHash: z.string().regex(/^[0-9a-f]{64}$/).optional() })).max(200).default([]),
    counts: z.object({ agents: z.number().int().nonnegative(), skills: z.number().int().nonnegative(), prompts: z.number().int().nonnegative(), instructions: z.number().int().nonnegative() }).optional(),
    /** The design systems installed on the machine, by catalogue id and the version it runs (design systems round 2). */
    designSystems: z.array(z.object({ id: z.string().max(100), version: z.string().max(40) })).max(50).optional(),
});
/**
 * A repository's board kept on this workbench (not promoted to the platform), as the workbench
 * reports it. `mirror` is the workbench's own GitHub, Azure Boards or Jira mirror of that board
 * when it is on: `target` is the identity a platform tracker uses (GitHub `owner/repo`, the Azure
 * DevOps project, the Jira project key); `since` is when the mirror was switched on.
 */
export const localBoardSchema = z.object({
    /** The board's name on the workbench, "" when it has only the one. */
    board: z.string().max(200).default(""),
    mirror: z.object({ provider: trackerProviderSchema, target: z.string().min(1).max(300), since: z.string().max(50).optional() }).optional(),
    /** Agents busy on the board now. */
    agents: z.number().int().nonnegative().max(10000).default(0),
});
export const workspaceSummarySchema = z.object({
    id: z.string().min(1).max(200),
    name: z.string().min(1).max(200),
    projectId: z.string().optional(),
    target: workspaceTargetSchema.optional(),
    /** The repository's remote and branch, so a member's clone is recorded on the project. */
    remote: z.string().max(500).optional(),
    branch: z.string().max(200).optional(),
    /** The host's state when the root is on a WSL distro or dev container; a stopped host's roots are still listed. */
    hostState: workspaceHostStateSchema.optional(),
    /** The WSL distro the root lives on, so the console can say which one is stopped. */
    distro: z.string().max(200).optional(),
    /** Whether an SSH host or node target answers. */
    targetState: workspaceTargetStateSchema.optional(),
    /** The workspace's customisations, when the workbench reports them (D219). */
    customisations: workspaceCustomisationsSchema.optional(),
    /** The repository's board when it is kept on this workbench (C-26); absent when it is not, or promoted to the platform. */
    localBoard: localBoardSchema.optional(),
    /**
     * AIOE 0.40.0 (additive): what the repository carries for agents, as the workbench read it from AGENTS.md / CLAUDE.md,
     * `.claude/` and `.mcp.json`. `fingerprint` is the tools inventory fingerprint of the same server (tools.ts `serverFingerprint`).
     */
    agentFiles: z
        .object({
        instructions: z.boolean(),
        hooks: z.number().int().nonnegative(),
        skills: z.number().int().nonnegative(),
        agents: z.number().int().nonnegative(),
        mcpServers: z.array(z.object({ name: z.string().max(200), kind: z.enum(["stdio", "http"]), fingerprint: z.string().max(200).optional() })).max(100),
        checkedAt: z.string().max(50),
        /** AIOE 0.52.0 (additive, workbench D272): the repository was set up for agents (`.mojoup/setup.json`): when, and the checklist rows applied. */
        setup: z.object({ at: z.string().max(50), rows: z.array(z.string().min(1).max(100)).max(100) }).optional(),
    })
        .optional(),
});
/**
 * A team or specialist a machine holds (AIOE 0.39.0, additive): what the workbench or node has loaded, so the
 * platform can say where each team runs. `scope` is where the team lives: `organisation` (the platform's,
 * granted to projects), `project` (the platform's, one project's), or `local` (this machine only, never saved
 * to the platform). `projectId` and `rootId` say which project or repository the machine runs it for.
 */
export const deviceTeamSchema = z.object({
    id: z.string().min(1).max(200),
    name: z.string().min(1).max(200),
    kind: z.enum(["team", "specialist"]).default("team"),
    scope: z.enum(["organisation", "project", "local"]),
    /** The platform's version of the team the machine holds; absent for a local team. */
    version: z.number().int().nonnegative().optional(),
    projectId: z.string().max(200).optional(),
    rootId: z.string().max(200).optional(),
    /** Its members in words (a person's display name, never an id), so a same-named team can be told apart at a glance. */
    members: z
        .array(z.object({ name: z.string().min(1).max(200), kind: z.enum(["agent", "person"]).default("agent"), runtime: z.string().max(100).optional(), role: z.string().max(200).optional() }))
        .max(50)
        .optional(),
    /** A `local` team: sent as true only when the person chose to keep it on this computer; absent when it is simply not published yet. */
    kept: z.literal(true).optional(),
});
/** `POST /fleet/v1/register` body: sent at start-up and whenever the workspace list changes. */
export const workbenchRegistrationSchema = z.object({
    schemaVersion: z.literal(FLEET_SCHEMA_VERSION),
    /** Stable per-install id minted by the desktop app and kept in its userData. */
    deviceId: z.string().min(1).max(100),
    hostname: z.string().min(1).max(200),
    platform: workbenchPlatformSchema,
    osVersion: z.string().max(200).optional(),
    appVersion: z.string().max(40),
    workspaces: z.array(workspaceSummarySchema).max(200),
    /** WireGuard public key of this device's mesh node, once enrolled. */
    meshPublicKey: z.string().max(100).optional(),
    /** Device posture (mesh round 3); absent reads as unknown. */
    posture: devicePostureSchema.optional(),
    /** Absent reads as "desktop", so registrations from before the field are unchanged. */
    kind: deviceKindSchema.default("desktop"),
    /** A node's manifest: what it offers (runtimes, models, memory, resources, roles). */
    capabilities: nodeManifestSchema.optional(),
    /** sha256 of the settings declarations this release runs, so the policy editor knows which keys the machine knows. */
    settingsDigest: z.string().regex(/^[0-9a-f]{64}$/).optional(),
    /** Versions of what the machine bundles besides the app itself: the mesh agent for now. */
    components: z.object({ mesh: z.string().max(40).optional() }).optional(),
    /** The policy manifest this build carries (policyManifest.ts): its version and digest (D175). */
    manifest: fleetManifestRefSchema.optional(),
    /**
     * Added for D177 phase 3 (AIOE 0.22.0, additive): this machine's sealing key's public half
     * (secretSeal.ts), kept as the device's key so a person's own secrets can be sealed to it.
     */
    sealingKey: deviceSealingKeySchema.optional(),
    /** D257 (AIOE 0.37.0, additive): what the machine holds: local models, runtimes, DLP state. The platform keeps the last. */
    facts: deviceFactsSchema.optional(),
    /** AIOE 0.39.0 (additive): the teams and specialists the machine holds. Absent: the platform keeps the last; empty: none. */
    teams: z.array(deviceTeamSchema).max(500).optional(),
});
/** What a node is running now, for the console's load view and the quotas. */
export const deviceLoadSchema = z.object({
    runningAgents: z.number().int().nonnegative(),
    /** Running agents per principal (a person's id), once hosts stamp who dispatched the work. */
    byPrincipal: z
        .record(z.string().min(1).max(200), z.number().int().nonnegative())
        .refine((r) => Object.keys(r).length <= 500, { message: "at most 500 principals" })
        .optional(),
});
/** `POST /fleet/v1/heartbeat` body: small, frequent. */
export const workbenchHeartbeatSchema = z.object({
    schemaVersion: z.literal(FLEET_SCHEMA_VERSION),
    deviceId: z.string().min(1).max(100),
    at: z.string(),
    activeAgents: z.number().int().nonnegative(),
    relay: z.enum(["off", "enrolling", "connecting", "connected", "error"]),
    policyRevision: z.string().optional(),
    catalogueRevision: z.string().optional(),
    settingsDigest: z.string().regex(/^[0-9a-f]{64}$/).optional(),
    /** `manifestRevision()` of the capabilities last registered; a different one means "register again". */
    capabilitiesRevision: z.string().max(100).optional(),
    load: deviceLoadSchema.optional(),
    /** The organisation's registry orders this machine applied and the specialists it holds stopped (agentRegistry.ts); absent until it has applied one. */
    agents: fleetAgentsReportSchema.optional(),
    /** The policy manifest this build carries: its version and digest (D175). */
    manifest: fleetManifestRefSchema.optional(),
    /** The applied organisation policy as this build understood it: the keys it does not know (D175). Absent when there are none. */
    policy: fleetPolicyReportSchema.optional(),
    /** Added for D177 phase 3 (AIOE 0.22.0, additive): the sealing key, as on the registration; a key made since is learnt within a minute. */
    sealingKey: deviceSealingKeySchema.optional(),
    /** C-26 (additive): the boards kept here, refreshed between registrations. An entry replaces the standing for that project and repository; `localBoard` absent clears it. */
    localBoards: z.array(z.object({ projectId: z.string().min(1).max(200), rootId: z.string().min(1).max(200), localBoard: localBoardSchema.optional() })).max(200).optional(),
    /** D257 (additive): the answers to device orders the machine has finished (see the header). */
    orders: z.array(deviceOrderAnswerSchema).max(200).optional(),
    /** D257 (additive): the machine's facts, as on the registration, when they moved. */
    facts: deviceFactsSchema.optional(),
    /** D282 (additive): the sessions open to remote control on the machine now (`[]` none; absent: a workbench before D282). */
    remoteSessions: remoteSessionsReportSchema.optional(),
    /** D313 teams-one-owner (AIOE 0.58.0, additive): local team definitions in managed projects still to reconcile ("not under management yet"); absent: a build before D313. */
    unmanagedTeams: z.number().int().nonnegative().max(10_000).optional(),
});
/** `POST /fleet/v1/heartbeat` answer. `update` is a pushed update or rollback (releases.ts); absent means nothing is ordered. */
export const heartbeatAnswerSchema = z.object({
    ok: z.literal(true),
    online: z.boolean(),
    /** The node's manifest changed since it last registered: register again. */
    reregister: z.boolean().optional(),
    update: updateOrderSchema.optional(),
    /** Registry orders (agentRegistry.ts): disable, quarantine or enable a specialist; repeated until the machine reports them applied. */
    agents: z.array(fleetAgentCommandSchema).max(MAX_AGENT_COMMANDS).optional(),
    /** The organisation holds no manifest for this machine's digest and asks for it (D175): `POST /fleet/v1/policy-manifest`. */
    policyManifest: policyManifestOrderSchema.optional(),
    /** AIOE 0.36.0: workspaces whose project was merged away, and where it went (see the header). */
    merged: z.array(z.object({ projectId: z.string(), mergedInto: z.string(), mergedIntoName: z.string() })).max(200).optional(),
    /** D257 (AIOE 0.37.0): the organisation's pending orders for this machine, oldest first (deviceOrders.ts); repeated until answered or expired. */
    orders: z.array(deviceOrderSchema).max(MAX_DEVICE_ORDERS).optional(),
    /** AIOE 0.59.0 (deviceClear.ts): delete what this machine holds of the organisation, then acknowledge it. */
    clear: deviceClearSchema.optional(),
});
/** Who a device belongs to: a person (a desktop), the organisation, or one project (nodes). */
export const deviceOwnerSchema = z.object({
    type: z.enum(["user", "org", "project"]),
    id: z.string().optional(),
});
/**
 * Whether a device is live. A desktop is approved when its user completes the code. A node is
 * `pending` until someone with `nodes:approve` signs off, `pending-surface` when its manifest grew
 * past what was approved (the old surface stays in force), and `refused` when an approver said no.
 */
export const deviceApprovalSchema = z.object({
    state: z.enum(["pending", "approved", "pending-surface", "refused"]),
    by: z.string().optional(),
    at: z.string().optional(),
    /** `manifestRevision()` of the surface that was approved. */
    surfaceRevision: z.string().optional(),
});
/** What the console reads: one row per device. */
export const fleetDeviceSchema = z.object({
    deviceId: z.string(),
    tenant: z.string(),
    /** Entra object id of the user who enrolled it. */
    userId: z.string(),
    userEmail: z.string().optional(),
    hostname: z.string(),
    platform: workbenchPlatformSchema,
    /** The operating system's version, as the machine registered it. */
    osVersion: z.string().optional(),
    appVersion: z.string(),
    workspaces: z.array(workspaceSummarySchema),
    registeredAt: z.string(),
    lastSeenAt: z.string(),
    online: z.boolean(),
    activeAgents: z.number().int().nonnegative(),
    policyRevision: z.string().optional(),
    /** The settings declarations the machine last reported running. */
    settingsDigest: z.string().optional(),
    meshPublicKey: z.string().optional(),
    meshAddress: z.string().optional(),
    kind: deviceKindSchema,
    capabilities: nodeManifestSchema.optional(),
    capabilitiesRevision: z.string().optional(),
    components: z.object({ mesh: z.string().optional() }).optional(),
    owner: deviceOwnerSchema,
    /** The approver of a node: accountable for it, not its owner. */
    sponsorUserId: z.string().optional(),
    approval: deviceApprovalSchema,
    tags: z.array(z.string()),
    /** D447: the approved cluster and node pool a node joined with, and the one project it is held to (a project's own join key, a GitHub run's). */
    compute: z.object({ clusterId: z.string().optional(), nodePool: z.string().optional(), projectId: z.string().optional() }).optional(),
    load: deviceLoadSchema.optional(),
    /** The machine's last registry report (agentRegistry.ts): what it holds stopped and which orders it applied. */
    agents: fleetAgentsReportSchema.optional(),
    /** The policy manifest the machine last reported running (policyManifest.ts). */
    manifest: fleetManifestRefSchema.optional(),
    /** Dotted paths in the applied policy the machine does not know: it is too old for them. */
    policyUnknownKeys: z.array(z.string()).optional(),
    /** The posture the machine last reported (mesh round 3); absent: unknown. */
    posture: devicePostureSchema.optional(),
    /** D257 (AIOE 0.37.0): the machine's last reported facts (deviceOrders.ts). */
    facts: deviceFactsSchema.optional(),
    /** AIOE 0.39.0: the teams and specialists the machine last reported holding. */
    teams: z.array(deviceTeamSchema).optional(),
    /** D282: the sessions the machine last reported open to remote control; only the device's own person and administrators see it. */
    remoteSessions: remoteSessionsReportSchema.optional(),
    /** D313 (AIOE 0.58.0): local team definitions the machine last reported as not under the organisation's management; absent: never reported. */
    unmanagedTeams: z.number().int().nonnegative().optional(),
});
/** `GET /fleet/v1/devices?for=me`: a workbench a member may run a team member on (another member's desktop). */
export const deviceForMeSchema = z.object({
    deviceId: z.string(),
    kind: z.literal("desktop"),
    name: z.string(),
    owner: z.object({ principal: z.string(), name: z.string().optional() }),
    online: z.boolean(),
    /** The projects the caller shares with its person. */
    projects: z.array(z.string()),
    /** Runtimes the workbench reported (its registration's capabilities), when it did. */
    runtimes: z.array(z.string()),
    lastSeenAt: z.string(),
});
/**
 * What `POST /fleet/v1/register` answers: the machine as the console sees it, the policy manifest
 * order when the platform wants this build's manifest, and (cost round 3, optional) `pricingDigest`,
 * the digest of the organisation's price list (pricing.ts). A workbench re-reads
 * `GET /catalogue/v1/pricing` when it differs from the digest it holds; absent on older platforms.
 *
 * `person` (AIOE 0.25.0, optional): the person the device token works for, as `GET /people/v1/me`
 * answers them — the same `id` and the organisation's verified `email` (on Enterprise the email the
 * sign-in's token carried, as the directory records it; on Cloud a sign-in method's proved email,
 * never an unproved one), so a workbench can check `/people/v1/me`.id === `person.id` before it hands
 * the email to a cloud agent. Absent for a node (it works for nobody in particular) and on older platforms.
 */
export const registrationPersonSchema = z.object({
    id: z.string().min(1).max(200),
    email: z.string().max(320).optional(),
    name: z.string().max(200).optional(),
});
export const registrationAnswerSchema = fleetDeviceSchema.extend({
    policyManifest: policyManifestOrderSchema.optional(),
    pricingDigest: z.string().regex(/^[0-9a-f]{64}$/).optional(),
    person: registrationPersonSchema.optional(),
});
//# sourceMappingURL=fleet.js.map